import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  TextField
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import React, { useState } from 'react';

import { useGetFocusAreaDueResponseCounts } from '@/features/engagement/engagement.services';
import { FocusAreaDueResponseCountDto, SignalResponseDTO } from '@/features/engagement/engagement.types';
import { iconsList } from '@/icons/MUIIconPicker';
import FocusAreaAllResponsesPopover from '@/layouts/socialFeed/FocusAreaAllResponsesPopover';
import MemberUpdateActivityPanel, { ResponseFilter } from '@/layouts/socialFeed/MemberUpdateActivityPanel';
import WeavrStarLogo from '@/public/svgs/new_logo_Star.svg';
import PanelTopIcon from '@/public/svgs/panel-top.svg';
import { useSocialFeedStore } from '@/store/useSocialFeedStore';

interface IProps {
  milestones?: readonly React.ReactNode[];
  recentLeadershipActivity?: readonly React.ReactNode[];
}

const getValidItems = (items: readonly React.ReactNode[] | undefined) =>
  React.Children.toArray(items).filter((item) => typeof item !== 'string' || item.trim().length > 0);

const SidebarContentSection = ({ title, items }: { title: string; items: React.ReactNode[] }) => (
  <>
    <Divider />
    <div className="flex flex-col gap-2">
      <div>{title}</div>
      {items}
    </div>
  </>
);

const SocialFeedSidebar = ({ milestones, recentLeadershipActivity }: IProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [deliverableSearch, setDeliverableSearch] = useState('');
  const [responseFilter, setResponseFilter] = useState<ResponseFilter>('all');
  const [expandedSignalDeliverableIds, setExpandedSignalDeliverableIds] = useState<Set<string>>(new Set());
  const [showMoreAnchorEl, setShowMoreAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedFocusAreaForMore, setSelectedFocusAreaForMore] = useState<FocusAreaDueResponseCountDto | null>(null);
  const { selectedFocusAreaId, setSelectedFocusAreaId, setDefaultCreatePostFocusAreaId, openCreatePostModal } =
    useSocialFeedStore();

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const { data: focusAreaDueResponseCounts } = useGetFocusAreaDueResponseCounts();
  const validMilestones = getValidItems(milestones);
  const validRecentLeadershipActivity = getValidItems(recentLeadershipActivity);
  const normalisedDeliverableSearch = deliverableSearch.trim().toLowerCase();
  const completedResponseCount = (focusAreaDueResponseCounts || []).reduce(
    (total, focusArea) => total + focusArea.completedDueCount,
    0
  );
  const incompleteResponseCount = (focusAreaDueResponseCounts || []).reduce(
    (total, focusArea) => total + Math.max(0, focusArea.dueCount - focusArea.completedDueCount),
    0
  );
  const isSearchingDeliverables = normalisedDeliverableSearch.length > 0;
  const visibleFocusAreas = (focusAreaDueResponseCounts || [])
    .map((item) => ({
      ...item,
      deliverables: (isSearchingDeliverables
        ? (item.deliverables || []).filter((deliverable) =>
            `${deliverable.order} ${deliverable.deliverableName}`.toLowerCase().includes(normalisedDeliverableSearch)
          )
        : item.deliverables || []
      )
        .map((deliverable) => ({
          ...deliverable,
          progressResponses: (deliverable.progressResponses || []).filter((response) =>
            responseFilter === 'completed'
              ? response.status === 2
              : responseFilter === 'incomplete'
                ? response.status !== 2
                : true
          )
        }))
        .filter((deliverable) => responseFilter === 'all' || deliverable.progressResponses.length > 0)
        .sort((a, b) => {
          const assignmentDifference = Number(b.assignedSignalCount > 0) - Number(a.assignedSignalCount > 0);
          return assignmentDifference || a.order.localeCompare(b.order, undefined, { numeric: true });
        })
    }))
    .filter((item) => item.deliverables.length > 0);

  const handleOpenSignalResponse = (item: FocusAreaDueResponseCountDto, response: SignalResponseDTO) => {
    setSelectedFocusAreaId(item.id);
    setDefaultCreatePostFocusAreaId(item.id);
    openCreatePostModal('text', {
      progressResponseId: response.progressResponseId,
      questionId: response.questionId,
      focusAreaId: item.id,
      businessUnitId: response.businessUnitId,
      signal: response.signal,
      type: response.type,
      responseOptions: response.responseOptions || [],
      businessUnits: response.businessUnits || [],
      responsePeriod: response.scheduledPeriod,
      availablePeriods: response.availablePeriods || []
    });
  };

  const handleOpenDeliverablePost = (
    item: FocusAreaDueResponseCountDto,
    deliverable: FocusAreaDueResponseCountDto['deliverables'][number]
  ) => {
    setSelectedFocusAreaId(item.id);
    setDefaultCreatePostFocusAreaId(item.id);
    openCreatePostModal('text', undefined, {
      deliverableId: deliverable.deliverableId,
      deliverableName: deliverable.deliverableName,
      focusAreaId: item.id
    });
  };

  const handleOpenQuestionPost = (
    item: FocusAreaDueResponseCountDto,
    question: FocusAreaDueResponseCountDto['deliverables'][number]['optionalSignals'][number]
  ) => {
    setSelectedFocusAreaId(item.id);
    setDefaultCreatePostFocusAreaId(item.id);
    const availablePeriod =
      (question.availablePeriods || []).find((period) => dayjs(period.scheduledPeriod).isSame(dayjs(), 'month')) ||
      question.availablePeriods?.[0];
    if (availablePeriod) {
      openCreatePostModal('text', {
        progressResponseId: availablePeriod.progressResponseId,
        questionId: question.questionId,
        focusAreaId: item.id,
        businessUnitId: availablePeriod.businessUnitId,
        signal: question.signal,
        type: question.type,
        responseOptions: question.responseOptions || [],
        businessUnits: question.businessUnits || [],
        responsePeriod: availablePeriod.scheduledPeriod,
        availablePeriods: question.availablePeriods || []
      });
      return;
    }
    openCreatePostModal('text', {
      progressResponseId: '',
      questionId: question.questionId,
      focusAreaId: item.id,
      businessUnitId: '',
      signal: question.signal,
      type: question.type,
      responseOptions: question.responseOptions || [],
      businessUnits: question.businessUnits || [],
      availablePeriods: []
    });
  };

  const toggleOptionalSignals = (deliverableId: string) => {
    setExpandedSignalDeliverableIds((current) => {
      const next = new Set(current);
      if (next.has(deliverableId)) next.delete(deliverableId);
      else next.add(deliverableId);
      return next;
    });
  };

  const handleOpenShowMore = (event: React.MouseEvent<HTMLButtonElement>, focusArea: FocusAreaDueResponseCountDto) => {
    event.stopPropagation();
    setSelectedFocusAreaForMore(focusArea);
    setShowMoreAnchorEl(event.currentTarget);
  };

  const handleCloseShowMore = () => {
    setShowMoreAnchorEl(null);
    setSelectedFocusAreaForMore(null);
  };

  return (
    <Box
      sx={{
        width: isOpen ? { xs: 361, lg: 420, xl: 440 } : 64,
        flexShrink: 0,
        px: isOpen ? '18px' : '12px',
        py: '15px',
        borderLeft: '1px solid',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: isOpen ? '10px' : '16px',
        transition: 'width 0.3s ease-in-out',
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(218, 101, 19, 0.1)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#da6513',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: '#c55a11'
          }
        }
      }}
    >
      <div className="flex w-full items-center justify-between">
        {isOpen ? (
          <img src={PanelTopIcon} alt="panel-top-icon" className="h-5 w-5 cursor-pointer" onClick={toggleSidebar} />
        ) : (
          <img src={WeavrStarLogo} alt="panel-top-icon" className="h-12 w-12 cursor-pointer" onClick={toggleSidebar} />
        )}
      </div>

      {isOpen && (
        <>
          <MemberUpdateActivityPanel
            completedResponseCount={completedResponseCount}
            incompleteResponseCount={incompleteResponseCount}
            responseFilter={responseFilter}
            onResponseFilterChange={setResponseFilter}
          />
          <Divider />
          <div className="flex flex-col items-start gap-2" data-tour="focus-area-section">
            <div className="flex w-full items-center justify-between gap-2" data-tour="focus-area-heading">
              <span className="font-semibold text-[#1C2024]">Focus Areas</span>
              <a
                target="_blank"
                href="https://weavrau.zendesk.com/hc/en-au/articles/16040719173263-Focus-Areas"
                className="whitespace-nowrap text-xs text-[#B95312]"
                rel="noreferrer"
              >
                Learn more
              </a>
            </div>
            <div className="flex w-full items-center gap-1.5">
              <TextField
                data-tour="deliverable-search"
                fullWidth
                size="small"
                value={deliverableSearch}
                onChange={(event) => setDeliverableSearch(event.target.value)}
                placeholder="Search Deliverables"
                slotProps={{
                  htmlInput: { 'aria-label': 'Search active Deliverables' },
                  input: {
                    endAdornment: deliverableSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear Deliverable search"
                          onClick={() => setDeliverableSearch('')}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined
                  }
                }}
              />
              {(deliverableSearch || responseFilter !== 'all') && (
                <Button
                  size="small"
                  onClick={() => {
                    setDeliverableSearch('');
                    setResponseFilter('all');
                  }}
                  sx={{ minWidth: 'auto', px: 0.5, textTransform: 'none' }}
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex w-full flex-col gap-2">
              {visibleFocusAreas.map((item) => {
                const IconComponent = iconsList.find((q) => q.name === item.icon)?.icon ?? undefined;
                const isSelected = selectedFocusAreaId === item.id;
                const deliverables = item.deliverables || [];
                const hasSidebarItems = deliverables.length > 0;

                return (
                  <Accordion
                    key={item.title}
                    expanded={hasSidebarItems && (isSearchingDeliverables || isSelected)}
                    onChange={() => {
                      const nextFocusAreaId = isSelected ? null : item.id;
                      setSelectedFocusAreaId(nextFocusAreaId);

                      // Case 1: no outstanding signals -> preset selected focus area for new posts.
                      if (nextFocusAreaId) {
                        setDefaultCreatePostFocusAreaId(nextFocusAreaId);
                        return;
                      }

                      setDefaultCreatePostFocusAreaId(null);
                    }}
                    disableGutters
                    sx={{
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        minHeight: 28,
                        height: 28,
                        px: 0,
                        '& .MuiAccordionSummary-content': {
                          m: 0
                        },
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                          m: 0
                        }
                      }}
                      expandIcon={undefined}
                    >
                      <div
                        data-tour="focus-area-counts"
                        className="flex h-7 w-full items-center justify-between rounded-lg px-2"
                        style={{
                          color: item.textColor || '#000000',
                          backgroundColor: item.backgroundColor,
                          border: '1px solid',
                          borderColor: isSelected ? item.textColor || '#000000' : 'transparent',
                          boxShadow: isSelected ? `0 0 3px 0.2px ${item.textColor || '#000000'}` : 'none'
                        }}
                      >
                        <div className="flex items-center gap-1">
                          {IconComponent ? (
                            <IconComponent className="h-4" style={{ color: item.iconColor }} />
                          ) : undefined}
                          <span className="flex-1 text-xs font-medium">{item.title}</span>
                        </div>
                        <span
                          className="whitespace-nowrap text-[10px] font-semibold"
                          title={`${item.distinctSignalCount} assigned Signals; ${item.dueUnrespondedCount} awaiting an update`}
                        >
                          {item.distinctSignalCount} assigned · {item.dueUnrespondedCount} waiting
                        </span>
                      </div>
                    </AccordionSummary>

                    {hasSidebarItems && (
                      <AccordionDetails className="ml-2 flex flex-col gap-1 rounded-md border border-[#E7E7E7] bg-white p-2">
                        {deliverables.map((deliverable) => {
                          const isAssigned = deliverable.assignedSignalCount > 0;
                          const signalsExpanded = expandedSignalDeliverableIds.has(deliverable.deliverableId);
                          const StrategyCategoryIcon = iconsList.find(
                            (icon) => icon.name === deliverable.strategyCategory?.icon
                          )?.icon;
                          return (
                            <div
                              data-tour={isAssigned ? 'assigned-deliverable' : 'unassigned-deliverable'}
                              key={deliverable.deliverableId}
                              className="cursor-pointer rounded-md border border-[#E5E7EB] p-2"
                              style={{
                                backgroundColor: isAssigned ? item.backgroundColor || '#E5EEF8' : '#F3F4F6',
                                opacity: isAssigned ? 1 : 0.72
                              }}
                              onClick={() => handleOpenDeliverablePost(item, deliverable)}
                            >
                              <div className="mb-1 flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <span
                                    title={deliverable.deliverableName}
                                    className="line-clamp-2 text-xs font-medium text-[#1F2937]"
                                  >
                                    {deliverable.order} {deliverable.deliverableName}
                                  </span>
                                  {isAssigned ? (
                                    <button
                                      data-tour="assigned-signal-toggle"
                                      type="button"
                                      aria-expanded={signalsExpanded}
                                      className="mt-0.5 block text-left text-[10px] font-medium text-[#5E6673] underline-offset-2 hover:underline"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        toggleOptionalSignals(deliverable.deliverableId);
                                      }}
                                    >
                                      {deliverable.assignedSignalCount} assigned Signal
                                      {deliverable.assignedSignalCount === 1 ? '' : 's'}
                                    </button>
                                  ) : (
                                    <span className="mt-0.5 block text-[10px] text-[#737373]">
                                      Not assigned · add your own update
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-0.5">
                                  {StrategyCategoryIcon && deliverable.strategyCategory && (
                                    <Tooltip title={deliverable.strategyCategory.title}>
                                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/70">
                                        <StrategyCategoryIcon
                                          sx={{ fontSize: 15, color: deliverable.strategyCategory.iconColor }}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}
                                  <IconButton
                                    size="small"
                                    aria-label="create unprompted deliverable post"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenDeliverablePost(item, deliverable);
                                    }}
                                    sx={{ p: 0.25, mt: '-2px' }}
                                  >
                                    <AddIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                {(deliverable.progressResponses || []).map((response) => {
                                  const isCompleted = response.status === 2;
                                  return (
                                    <div
                                      key={response.progressResponseId}
                                      className={`rounded-md border px-2 py-1.5 ${
                                        isCompleted
                                          ? 'border-[#9BC7B4] bg-[#EFF8F3]'
                                          : 'cursor-pointer border-[#E8C15A] bg-[#FFF8DF]'
                                      }`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (!isCompleted) handleOpenSignalResponse(item, response);
                                      }}
                                    >
                                      <span
                                        className={`mb-0.5 block text-[10px] font-semibold uppercase tracking-wide ${
                                          isCompleted ? 'text-[#2F6B4F]' : 'text-[#8A6415]'
                                        }`}
                                      >
                                        {isCompleted ? 'Completed' : 'Update due'} ·{' '}
                                        {dayjs(response.scheduledPeriod).format('MMM YYYY')}
                                      </span>
                                      <span className="block text-xs font-medium text-[#374151]">{response.signal}</span>
                                      {response.businessUnitName && (
                                        <span className="mt-1 block text-[11px] font-medium text-[#6B7280]">
                                          Business Unit: {response.businessUnitName}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                                {signalsExpanded && responseFilter === 'all' && (
                                  <div className="flex flex-col gap-1 pt-0.5">
                                    {(deliverable.optionalSignals || []).map((question) => (
                                      <button
                                        type="button"
                                        key={question.questionId}
                                        className="rounded-md border border-[#D1D5DB] bg-[#F3F4F6] px-2 py-1.5 text-left text-xs text-[#5E6673] hover:border-[#9CA3AF] hover:bg-[#E5E7EB]"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleOpenQuestionPost(item, question);
                                        }}
                                      >
                                        <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-[#737373]">
                                          Not due · add an update
                                        </span>
                                        {question.signal}
                                      </button>
                                    ))}
                                    {(deliverable.optionalSignals || []).length === 0 && (
                                      <span className="rounded-md bg-[#F3F4F6] px-2 py-1.5 text-[10px] text-[#737373]">
                                        All assigned Signals currently awaiting an update are shown above.
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {item.hasMoreDueResponses && (
                          <Button
                            size="small"
                            sx={{ mt: 1, alignSelf: 'flex-start', textTransform: 'none' }}
                            onClick={(event) => handleOpenShowMore(event, item)}
                          >
                            show more...
                          </Button>
                        )}
                      </AccordionDetails>
                    )}
                  </Accordion>
                );
              })}
              {isSearchingDeliverables && visibleFocusAreas.length === 0 && (
                <div
                  role="status"
                  className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs text-[#4B5563]"
                >
                  No active Deliverables match your search. Completed Deliverables can be viewed through Strategy.
                </div>
              )}
            </div>
          </div>
          {validMilestones.length > 0 && <SidebarContentSection title="Milestones" items={validMilestones} />}
          {validRecentLeadershipActivity.length > 0 && (
            <SidebarContentSection title="Recent Leadership Activity" items={validRecentLeadershipActivity} />
          )}
        </>
      )}
      <FocusAreaAllResponsesPopover
        anchorEl={showMoreAnchorEl}
        focusArea={selectedFocusAreaForMore}
        onClose={handleCloseShowMore}
        onOpenSignalResponse={handleOpenSignalResponse}
      />
    </Box>
  );
};

export default SocialFeedSidebar;
